import type { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@ctct/payflow-ui';
import { PageError } from '@ctct/presentation-layer';

interface Props extends PropsWithChildren {
  status: 'pending' | 'error' | 'loaded' | 'loaded-stripe' | 'loaded-buynow';
  skeletonHeight?: number;
  skeletonWidth?: number;
  skeleton?: React.ReactNode;
  errorMessage?: string;
  retry?: () => void;
  disablePending?: boolean;
  disableError?: boolean;
}

export const StatusError = ({ errorMessage, retry }: { errorMessage?: string; retry?: () => void }) => {
  const { t } = useTranslation();

  return (
    <PageError
      heading={t('with-status-handling.something-went-wrong')}
      description={errorMessage}
      retry={
        retry && {
          label: t('with-status-handling.retry'),
          fn: retry,
        }
      }
    />
  );
};

export const WithStatusHandling = ({
  children,
  skeletonHeight,
  skeletonWidth,
  skeleton = <Skeleton shape="rectangle" height={skeletonHeight} width={skeletonWidth} data-qe-id="skeleton" />,
  status,
  errorMessage,
  retry,
  disableError,
  disablePending,
}: Props) => {
  if (status === 'pending' && !disablePending) {
    return skeleton;
  }

  if (status === 'error' && !disableError) {
    return <StatusError errorMessage={errorMessage} retry={retry} />;
  }

  return children;
};
